import axios from 'axios';
import { API_ORIGIN } from './_apiBase';
import { InspectionDetail } from './inspectionsApi';

/**
 * Sidorna som nås via länk utan inloggning (avvikelse, guide, ledighet, egenkontroll).
 * Token i länken är nyckeln. Fel kastas vidare så sidorna kan visa "länken hittades inte" på 404.
 */

const pub = (path: string) => `${API_ORIGIN}/api/${path}`;

export const fetchDeviationForm = async <T = any>(token: string): Promise<T> =>
    (await axios.get<T>(pub(`deviation/${token}`))).data;

export const fetchDeviationHistory = async <T = any>(token: string): Promise<T[]> =>
    (await axios.get<T[]>(pub(`deviation/${token}/history`))).data;

export const submitDeviationForm = async <T = any>(token: string, body: Record<string, unknown>): Promise<T> =>
    (await axios.post<T>(pub(`deviation/${token}`), body)).data;

export const fetchGuide = async <T = any>(token: string): Promise<T> =>
    (await axios.get<T>(pub(`guide/${token}`))).data;

export const fetchGuideHistory = async <T = any>(token: string): Promise<T[]> =>
    (await axios.get<T[]>(pub(`guide/${token}/history`))).data;

export const fetchOffhoursDigest = async <T = any>(token: string): Promise<T> =>
    (await axios.get<T>(pub(`offhours/${token}`))).data;

// contact_id = ?c i länken: talar om vem som öppnat sidan när en grupp är ansvarig.
export const fetchPublicInspection = async (token: string, contactId?: number | null): Promise<InspectionDetail> =>
    (await axios.get<InspectionDetail>(pub(`inspection/${token}`), { params: contactId ? { c: contactId } : {} })).data;

export const submitPublicInspection = async (token: string, body: Record<string, unknown>): Promise<InspectionDetail> =>
    (await axios.post<InspectionDetail>(pub(`inspection/${token}`), body)).data;

export const savePublicInspectionDraft = async (token: string, body: { answers: Record<string, unknown>; note?: string; name?: string; contact_id?: number | null }): Promise<InspectionDetail> =>
    (await axios.post<InspectionDetail>(pub(`inspection/${token}/draft`), body)).data;

/** Rondsidan: platserna i ronden med status. */
export interface PublicRound {
    id: number;
    token: string;
    due_date: string;
    schedule_id: number;
    schedule_name: string;
    template_name: string;
    group_owned: boolean;
    responsible_group_name: string | null;
    total: number;
    done: number;
    items: {
        id: number; token: string; zone_id: number | null; zone_name: string | null;
        status: 'pending' | 'overdue' | 'done' | 'action' | 'skipped';
        has_draft: boolean; has_deviation: boolean; no_need: boolean;
        started_by_contact_id: number | null; started_by_name: string | null; completed_by_name: string | null;
    }[];
    viewer: { id: number; name: string } | null;
}

export const fetchPublicRound = async (token: string, contactId?: number | null): Promise<PublicRound> =>
    (await axios.get<PublicRound>(pub(`round/${token}`), { params: contactId ? { c: contactId } : {} })).data;

/** Påbörjar kontrollen (eller tar över den med takeover). 409 om någon annan redan håller på. */
export const startPublicInspection = async (token: string, body: { name: string; contact_id?: number | null; takeover?: boolean }): Promise<InspectionDetail> =>
    (await axios.post<InspectionDetail>(pub(`inspection/${token}/start`), body)).data;

export const updatePublicInspectionActionDue = async (token: string, action_due_date: string, contactId?: number | null): Promise<InspectionDetail> =>
    (await axios.post<InspectionDetail>(pub(`inspection/${token}/action-due`), { action_due_date, contact_id: contactId ?? null })).data;

export const finishPublicInspectionAction = async (token: string, body: { action_done_note: string; action_done_by_name: string; contact_id?: number | null }): Promise<InspectionDetail> =>
    (await axios.post<InspectionDetail>(pub(`inspection/${token}/action-done`), body)).data;
